import { useDataValidation } from '@/composables/useDataValidation';
import { getPanelDefaults } from '@/constants/settingPanels';
import { useAssetStore } from '@/stores/api/asset.js';
import { createBaseStore } from '@/stores/api/base.js';
import { useSelectionStore } from '@/stores/ui/selection';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer.js';
import { render } from '@/threejs/core';
import { animationManager } from '@/threejs/managers/animationManager';
import { useRefHistory } from '@vueuse/core';
import { defineStore } from 'pinia';
import { watch } from 'vue';

export const useCruxModuleStore = defineStore('crux-module', () => {
  const timelinePlayerStore = useTimelinePlayerStore();
  const { sanitizeModuleData } = useDataValidation();

  const {
    items: cruxModules,
    fetchItems: fetch,
    storeItem: store,
    updateItem: update,
    destroyItem: destroy,
  } = createBaseStore('api/crux/module');

  const { history, undo, redo, clear, canRedo, canUndo, isTracking, pause, resume, commit } = useRefHistory(
    cruxModules,
    { deep: true, flush: 'post' }
  );

  const fetchforAsset = async (assetId) => {
    const overrideUrl = `/api/crux/module?asset=${assetId}&page_size=1000`;
    const response = await fetch(null, overrideUrl);

    cruxModules.value.forEach(sanitizeModuleData);

    // Explicitly sync after fetching to ensure animation manager is updated
    syncTimelineWithAnimationManager();

    return response;
  };

  const addSceneModule = async (cruxCode, type, data, name) => {
    const assetStore = useAssetStore();
    const selectionStore = useSelectionStore();

    if (!cruxCode) {
      cruxCode = assetStore.currentAsset.latest_version.code;
    }
    const capitalizedShape = data?.shape ? data.shape.charAt(0).toUpperCase() + data.shape.slice(1) : '';
    const module = sanitizeModuleData({
      crux_version: cruxCode,
      name: name || `New ${capitalizedShape || type}`,
      type: type,
      data: { ...data },
    });

    const response = await store(module);
    selectionStore.setSelectedObject(response.code ?? null);
    return response;
  };

  const duplicate = async (code) => {
    const module = cruxModules.value.find((m) => m.code === code);

    if (module) {
      const newModule = sanitizeModuleData({
        ...module,
        name: `${module.name} Copy`,
      });

      return await store(newModule);
    }

    return null;
  };

  const addTimeLineLayerModule = async (cruxCode, data) => {
    const module = sanitizeModuleData({
      crux_version: cruxCode,
      name: `New Timeline Layer`,
      type: 'timeline_layer',
      data: { ...data },
    });

    return await store(module);
  };

  const addKeyFrametoTimelineLayer = async (cruxCode, objectCode, property, time, value) => {
    const module = cruxModules.value.find(
      (module) =>
        module.type === 'timeline_layer' &&
        module.crux_version === cruxCode &&
        module.data.objectCode === objectCode &&
        module.data.property === property
    );

    if (module) {
      const keyframes = module.data.keyframes || [];
      // Check if a keyframe already exists at this time
      const existingKeyframe = keyframes.find((kf) => kf.time === time);

      if (existingKeyframe) {
        return existingKeyframe; // Skip adding if a keyframe already exists at this time
      }

      keyframes.push({ time, value });
      // Sort keyframes by time
      keyframes.sort((a, b) => a.time - b.time);
      const updatedData = { ...module.data, keyframes };
      const updatedModule = sanitizeModuleData({ ...module, data: updatedData });

      return await update(module.code, updatedModule);
    } else {
      const newModule = sanitizeModuleData({
        crux_version: cruxCode,
        name: `Timeline Layer for ${objectCode} - ${property}`,
        type: 'timeline_layer',
        data: {
          objectCode,
          property,
          keyframes: [{ time, value }],
        },
      });

      return await store(newModule);
    }
  };

  const hasKeyframe = (objectId, key) => {
    const layer = cruxModules.value.find(
      (module) =>
        module.type === 'timeline_layer' && module.data.objectCode === objectId && module.data.property === key
    );

    return layer ? layer.data.keyframes.length > 0 : false;
  };

  const getKeyframeAtCurrentTime = (objectId, key) => {
    const currentTime = timelinePlayerStore.currentTime;
    const layer = cruxModules.value.find(
      (module) =>
        module.type === 'timeline_layer' && module.data.objectCode === objectId && module.data.property === key
    );
    if (layer) {
      return layer.data.keyframes.find((kf) => kf.time === currentTime);
    }
    return null;
  };

  const getKeyframeAtCurrent = (objectId, time, key) => {
    const layer = cruxModules.value.find(
      (module) =>
        module.type === 'timeline_layer' && module.data.objectCode === objectId && module.data.property === key
    );
    if (layer) {
      return layer.data.keyframes.find((kf) => kf.time === time);
    }
    return null;
  };

  const setKeyframeAtCurrent = async (objectId, time, key, value) => {
    const layer = cruxModules.value.find(
      (module) =>
        module.type === 'timeline_layer' && module.data.objectCode === objectId && module.data.property === key
    );

    if (layer) {
      const keyframe = layer.data.keyframes.find((kf) => kf.time === time);
      if (keyframe) {
        keyframe.value = value;
      } else {
        layer.data.keyframes.push({ time, value });
        // Sort keyframes by time
        layer.data.keyframes.sort((a, b) => a.time - b.time);
      }

      const response = await update(layer.code, layer);
      // Update the scene to reflect the keyframe change
      animationManager.updateObjects();
      render();
      return response;
    }

    return null;
  };

  const deleteKeyframeAtTime = async (objectId, time, key) => {
    const layer = cruxModules.value.find(
      (module) =>
        module.type === 'timeline_layer' && module.data.objectCode === objectId && module.data.property === key
    );

    if (layer) {
      const keyframeIndex = layer.data.keyframes.findIndex((kf) => kf.time === time);
      if (keyframeIndex !== -1) {
        layer.data.keyframes.splice(keyframeIndex, 1);
        return await update(layer.code, layer);
      }
    }

    return null;
  };

  const updateKeyframeTime = async (objectId, index, newTime) => {
    const layer = cruxModules.value.find((module) => module.type === 'timeline_layer' && module.code === objectId);

    if (layer) {
      const keyframe = layer.data.keyframes[index];
      if (keyframe) {
        keyframe.time = newTime;
        // Sort keyframes by time
        layer.data.keyframes.sort((a, b) => a.time - b.time);

        return await update(layer.code, layer);
      }
    }

    return null;
  };

  const mergeSettingModule = async (cruxCode, type, key, value) => {
    const existingModule = cruxModules.value.find(
      (module) => module.type === type && (cruxCode ? module.crux_version === cruxCode : true)
    );

    if (existingModule) {
      if (existingModule.data[key] === value) {
        return existingModule;
      }

      const updatedData = { ...existingModule.data, [key]: value };
      const updatedModule = sanitizeModuleData({
        ...existingModule,
        data: updatedData,
      });

      return await update(existingModule.code, updatedModule);
    } else {
      const module = sanitizeModuleData({
        crux_version: cruxCode,
        name: `Setting ${type}`,
        type: type,
        data: { [key]: value },
      });

      return await store(module);
    }
  };

  const updateModuleData = async (moduleType, key, value) => {
    const existingModule = cruxModules.value.find((module) => module.type === moduleType);

    if (existingModule) {
      if (existingModule.data[key] === value) {
        return existingModule;
      }

      const updatedData = { ...existingModule.data, [key]: value };
      const updatedModule = sanitizeModuleData({
        ...existingModule,
        data: updatedData,
      });

      return await update(existingModule.code, updatedModule);
    } else {
      // Create new module if it doesn't exist
      const assetStore = useAssetStore();
      const cruxCode = assetStore.currentAsset?.latest_version?.code;

      const module = sanitizeModuleData({
        crux_version: cruxCode,
        name: `${moduleType}`,
        type: moduleType,
        data: { [key]: value },
      });

      return await store(module);
    }
  };

  const getSceneTreeModules = (parentCode) => {
    return cruxModules.value.filter((module) => module.data.parentCode === parentCode);
  };

  const getRootSceneTreeModules = () => {
    return cruxModules.value.filter(
      (module) =>
        !module.data.parentCode &&
        (module.type === 'projector' ||
          module.type === 'geometry' ||
          module.type === 'led_screen' ||
          module.type === 'model' ||
          module.type === 'puck' ||
          module.type === 'group')
    );
  };

  const filterModulesByType = (type) => {
    return cruxModules.value.filter((module) => module.type === type);
  };

  const getModuleByType = (type) => {
    return cruxModules.value.find((module) => module.type === type);
  };

  const getSettingValue = (type, key, defaultValue = null, cruxCode = null) => {
    const existingModule = cruxModules.value.find(
      (module) => module.type === type && (cruxCode ? module.crux_version === cruxCode : true)
    );

    if (existingModule && existingModule.data && existingModule.data[key] !== undefined) {
      return existingModule.data[key];
    }

    return defaultValue;
  };

  // Sync timeline layers with animation manager
  const syncTimelineWithAnimationManager = () => {
    // Clear existing animations
    animationManager.clear();

    // Filter timeline_layer modules and add to animation manager
    const timelineLayers = cruxModules.value.filter((module) => module.type === 'timeline_layer');
  };

  const createDefaultModulesForAsset = async (assetId) => {
    const grid = getPanelDefaults('grid');
    const render = getPanelDefaults('render');
    const brightness = getPanelDefaults('brightness');
    const defaultModules = [
      {
        type: 'viewport_settings',
        name: 'Default Viewport',
        data: {
          'grid.enabled': grid['grid.enabled'].default ?? true,
          'grid.size': grid['grid.size'].default ?? 10,
          'grid.subsections': grid['grid.subsections'].default ?? 10,
          'grid.color2': grid['grid.color2'].default ?? '#374151',
          'grid.color1': grid['grid.color1'].default ?? '#1F2937',
          'render.background': render['render.background'].default ?? '#111828',
          'render.background2': render['render.background2'].default ?? '#000000',
          'render.gradient': render['render.gradient'].default ?? false,
          'brightness.ambientLight': brightness['brightness.ambientLight'].default ?? true,
          'brightness.ambientLightIntensity': brightness['brightness.ambientLightIntensity'].default ?? 0.03,
          'brightness.directionalLight': brightness['brightness.directionalLight'].default ?? true,
          'brightness.directionalLightIntensity': brightness['brightness.directionalLightIntensity'].default ?? 0.05,
          'brightness.preset': 'moonlight',
        },
      },
    ];

    const createdModules = defaultModules.map((module) => {
      const newModule = {
        crux_version: assetId,
        name: module.name,
        type: module.type,
        data: module.data,
      };
      return store(newModule);
    });
    return await Promise.all(createdModules);
  };

  // Save all modules to the API (used after undo/redo)
  const save = async () => {
    try {
      // Pause history tracking during save to prevent creating new history entries
      pause();

      // Save each module directly to the API without updating the store
      const savePromises = cruxModules.value.map((module) => {
        if (module.code) {
          const sanitizedModule = sanitizeModuleData(module);
          update(module.code, sanitizedModule);
        }
      });
      await Promise.all(savePromises.filter(Boolean));

      // Resume history tracking after save
      resume();
    } catch (error) {
      console.error('Error saving modules:', error);
      // Make sure to resume even if there's an error
      resume();
      throw error;
    }
  };

  const clearStore = () => {
    clear();

    cruxModules.value = [];
  };

  // Watch for changes in cruxModules and sync with animation manager
  watch(
    cruxModules,
    () => {
      syncTimelineWithAnimationManager();
    },
    { deep: true }
  );

  return {
    cruxModules,
    fetch,
    destroy,
    update,
    store,
    duplicate,
    save,
    history,
    undo,
    redo,
    clear,
    canUndo,
    canRedo,
    pause,
    resume,
    commit,
    isTracking,
    filterModulesByType,
    fetchforAsset,
    addSceneModule,
    mergeSettingModule,
    updateModuleData,
    getSceneTreeModules,
    getRootSceneTreeModules,
    getModuleByType,
    getSettingValue,
    addTimeLineLayerModule,
    addKeyFrametoTimelineLayer,
    hasKeyframe,
    getKeyframeAtCurrentTime,
    getKeyframeAtCurrent,
    setKeyframeAtCurrent,
    deleteKeyframeAtTime,
    updateKeyframeTime,
    syncTimelineWithAnimationManager,
    createDefaultModulesForAsset,
    clearStore,
  };
});
